// ============================================================
// RESTAURANT MANAGEMENT PAGE — Restaurant listing with drill-down
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Download, Star, Users, ExternalLink } from 'lucide-react';
import { Table, Badge, Button, SearchInput, Select, Pagination, Modal, Avatar, StatCard } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getRestaurants } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDate, formatCurrency, downloadCSV } from '../utils/helpers';
import { toast } from 'react-toastify';
import { UtensilsCrossed, CheckCircle, Clock, XCircle } from 'lucide-react';

// ── Kitchen Users Panel ───────────────────────────────────────
const KitchenUsersPanel = ({ restaurant }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurant?.id) return;
    setLoading(true);
    adminApi.getKitchenUsers(restaurant.id)
      .then(res => { setUsers(Array.isArray(res) ? res : []); setLoading(false); })
      .catch(() => { setUsers([]); setLoading(false); });
  }, [restaurant?.id]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-text-muted)' }}>
      <div style={{ width: 32, height: 32, border: '3px solid rgba(232,93,4,0.3)', borderTopColor: '#E85D04', borderRadius: '50%', animation: 'admin-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <p>Loading kitchen users…</p>
    </div>
  );

  if (users.length === 0) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-text-muted)' }}>
      <Users size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
      <p style={{ fontWeight: 600 }}>No users linked to this kitchen</p>
    </div>
  );

  return (
    <div style={{ maxHeight: 380, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--admin-border)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--admin-bg)', position: 'sticky', top: 0, zIndex: 1 }}>
            {['User', 'Phone', 'Role', 'Verified', 'Last Login', 'Logins', 'Status'].map(h => (
              <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => (
            <tr key={u.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
              <td style={{ padding: '10px 12px' }}>
                <div style={{ fontWeight: 600 }}>{u.name}</div>
                <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>{u.email}</div>
              </td>
              <td style={{ padding: '10px 12px' }}>{u.phone}</td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: '#FFF7ED', color: '#C2410C', textTransform: 'capitalize' }}>{u.role}</span>
              </td>
              <td style={{ padding: '10px 12px' }}>
                {u.isVerified
                  ? <span style={{ color: '#10B981', fontWeight: 700 }}>✓ Yes</span>
                  : <span style={{ color: '#EF4444' }}>✗ No</span>}
              </td>
              <td style={{ padding: '10px 12px', color: 'var(--admin-text-secondary)', fontSize: 12 }}>
                {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : '—'}
              </td>
              <td style={{ padding: '10px 12px', fontWeight: 700 }}>{u.loginCount}</td>
              <td style={{ padding: '10px 12px' }}>
                {u.isActive
                  ? <span style={{ color: '#10B981', fontWeight: 700, fontSize: 12 }}>● Active</span>
                  : <span style={{ color: '#EF4444', fontWeight: 700, fontSize: 12 }}>● Inactive</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Menu Panel ────────────────────────────────────────────────
const MenuPanel = ({ restaurant }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | available | unavailable

  useEffect(() => {
    if (!restaurant?.id) return;
    setLoading(true);
    adminApi.getRestaurantMenu(restaurant.id, () => [])
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || res?.data || []);
        setItems(arr.map(i => ({
          id: i.item_id || i.id,
          name: i.item_name || i.name || 'Unnamed',
          category: i.category_name || i.category || '—',
          price: i.price || i.base_price || 0,
          isAvailable: i.is_available !== undefined ? i.is_available : (i.available !== undefined ? i.available : true),
          description: i.description || '',
          isVeg: i.food_type === 'veg' || i.is_vegetarian === true
            ? true
            : (i.food_type === 'non_veg' ? false : null),
        })));
        setLoading(false);
      })
      .catch(() => { setItems([]); setLoading(false); });
  }, [restaurant?.id]);

  const toggle = useCallback(async (item) => {
    const next = !item.isAvailable;
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, isAvailable: next } : x));
    try {
      await adminApi.toggleMenuItemAvailability(restaurant.id, item.id, next);
      toast.success(`"${item.name}" marked ${next ? 'available' : 'unavailable'}`);
    } catch {
      setItems(prev => prev.map(x => x.id === item.id ? { ...x, isAvailable: !next } : x));
      toast.error('Failed to update availability');
    }
  }, [restaurant?.id]);

  const displayed = filter === 'all' ? items
    : filter === 'available' ? items.filter(i => i.isAvailable)
    : items.filter(i => !i.isAvailable);

  const availableCount = items.filter(i => i.isAvailable).length;
  const unavailableCount = items.length - availableCount;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-text-muted)' }}>
      <div style={{ width: 32, height: 32, border: '3px solid rgba(232,93,4,0.3)', borderTopColor: '#E85D04', borderRadius: '50%', animation: 'admin-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <p>Loading menu…</p>
    </div>
  );

  if (items.length === 0) return (
    <div style={{ textAlign: 'center', padding: 40, color: 'var(--admin-text-muted)' }}>
      <UtensilsCrossed size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
      <p style={{ fontWeight: 600 }}>No menu items found</p>
      <p style={{ fontSize: 12, marginTop: 4 }}>This restaurant has not added any menu items yet.</p>
    </div>
  );

  return (
    <div>
      {/* Summary + filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ padding: '6px 14px', borderRadius: 8, background: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: 13 }}>
          ✅ {availableCount} Available
        </span>
        <span style={{ padding: '6px 14px', borderRadius: 8, background: '#FEE2E2', color: '#991B1B', fontWeight: 700, fontSize: 13 }}>
          ❌ {unavailableCount} Unavailable
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {['all', 'available', 'unavailable'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{
                padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                border: '1px solid var(--admin-border)',
                background: filter === f ? '#E85D04' : 'var(--admin-surface)',
                color: filter === f ? '#fff' : 'var(--admin-text-secondary)',
              }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ maxHeight: 340, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--admin-border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--admin-bg)', position: 'sticky', top: 0, zIndex: 1 }}>
              {['Item', 'Category', 'Price', 'Type', 'Status', 'Action'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.map((item, i) => (
              <tr key={item.id || i} style={{ borderBottom: '1px solid var(--admin-border)', opacity: item.isAvailable ? 1 : 0.65 }}>
                <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                  {item.name}
                  {item.description && (
                    <div style={{ fontSize: 11, color: 'var(--admin-text-muted)', marginTop: 2, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.description}
                    </div>
                  )}
                </td>
                <td style={{ padding: '10px 12px', color: 'var(--admin-text-secondary)' }}>{item.category}</td>
                <td style={{ padding: '10px 12px', fontWeight: 700, color: '#10B981' }}>{formatCurrency(item.price)}</td>
                <td style={{ padding: '10px 12px' }}>
                  {item.isVeg === true
                    ? <span style={{ color: '#16A34A', fontSize: 11, fontWeight: 700, background: '#DCFCE7', padding: '2px 8px', borderRadius: 4 }}>🟢 Veg</span>
                    : item.isVeg === false
                    ? <span style={{ color: '#DC2626', fontSize: 11, fontWeight: 700, background: '#FEE2E2', padding: '2px 8px', borderRadius: 4 }}>🔴 Non-Veg</span>
                    : <span style={{ color: 'var(--admin-text-muted)', fontSize: 11 }}>—</span>}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {item.isAvailable
                    ? <span style={{ color: '#10B981', fontWeight: 700, fontSize: 12 }}>✅ Available</span>
                    : <span style={{ color: '#EF4444', fontWeight: 700, fontSize: 12 }}>❌ Unavailable</span>}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <button onClick={() => toggle(item)}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
                      background: item.isAvailable ? '#FEE2E2' : '#D1FAE5',
                      color: item.isAvailable ? '#991B1B' : '#065F46',
                    }}>
                    {item.isAvailable ? 'Disable' : 'Enable'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RestaurantManagement = () => {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('kitchens'); // 'kitchens' | 'chefs'

  // ── Kitchens state ────────────────────────────────────────
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [statusFilter, setStatusFilter] = useState('');

  // ── Chefs state ───────────────────────────────────────────
  const [chefs, setChefs] = useState([]);
  const [chefsLoading, setChefsLoading] = useState(false);
  const [chefsError, setChefsError] = useState(null);
  const [selectedChef, setSelectedChef] = useState(null);
  const [chefRoleFilter, setChefRoleFilter] = useState('');

  useEffect(() => {
    adminApi.getRestaurants({ per_page: 500 }, getRestaurants)
      .then(res => {
        const raw = Array.isArray(res) ? res : (res?.items || []);
        const normalized = raw.map(r => {
          // operating_hours can be an object from the API — extract food_type safely
          const opHours = r.operating_hours;
          const cuisineRaw = r.cuisine || (typeof opHours === 'object' ? opHours?.food_type : opHours) || '—';
          const safeStr = (v, fallback = '—') => {
            if (v === null || v === undefined) return fallback;
            if (typeof v === 'object') return Object.values(v).filter(x => typeof x === 'string').join(', ') || fallback;
            return String(v) || fallback;
          };
          return {
            id: r.id || r.kitchen_id,
            name: safeStr(r.name || r.kitchen_name, 'Unknown'),
            owner: safeStr(r.owner || r.full_name || r.contact_person_name, '—'),
            email: safeStr(r.email || r.business_email, '—'),
            phone: safeStr(r.phone || r.contact_person_phone || r.contact_phone, '—'),
            city: safeStr(r.city || r.address_city, '—'),
            cuisine: safeStr(cuisineRaw, '—'),
            rating: r.rating ?? r.platform_score ?? 0,
            totalOrders: r.totalOrders ?? r.total_orders ?? 0,
            revenue: r.revenue ?? r.total_revenue ?? 0,
            commission: r.commission ?? r.commission_rate ?? 0,
            status: r.status === 'online' ? 'active' : (r.status === 'offline' ? 'inactive' : (r.status || 'inactive')),
            verified: r.verified ?? (r.verification_status === 'approved'),
            createdAt: r.createdAt || r.created_at,
          };
        });
        setAll(normalized);
        setLoading(false);
      })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  // Load chefs when tab is switched
  useEffect(() => {
    if (mainTab !== 'chefs') return;
    if (chefs.length > 0) return; // already loaded
    setChefsLoading(true);
    adminApi.getChefs({}, () => [])
      .then(res => {
        setChefs(Array.isArray(res) ? res : (res?.items || []));
        setChefsLoading(false);
      })
      .catch(e => { setChefsError(e.message || 'Failed to load chefs'); setChefsLoading(false); });
  }, [mainTab]);

  const { query, setQuery, filtered } = useSearch(all, ['name', 'owner', 'email', 'city', 'cuisine']);
  const final = statusFilter ? filtered.filter(r => r.status === statusFilter) : filtered;
  const { currentPage, totalPages, paginated, goTo } = usePagination(final);

  const { query: chefQuery, setQuery: setChefQuery, filtered: filteredChefs } = useSearch(chefs, ['name', 'email', 'phone', 'kitchenName', 'kitchenCity', 'role']);
  const finalChefs = chefRoleFilter ? filteredChefs.filter(c => c.role === chefRoleFilter) : filteredChefs;
  const { currentPage: chefPage, totalPages: chefTotalPages, paginated: paginatedChefs, goTo: goToChef } = usePagination(finalChefs);

  const toggleStatus = async (r) => {
    const newStatus = r.status === 'active' ? 'inactive' : 'active';
    try {
      await adminApi.toggleRestaurantStatus(r.id, newStatus);
    } catch { /* fallback to local update */ }
    setAll(p => p.map(x => x.id === r.id ? { ...x, status: newStatus } : x));
    toast.success(`Restaurant ${r.status === 'active' ? 'deactivated' : 'activated'}`);
  };

  const toggleChefStatus = async (chef) => {
    const newStatus = chef.isActive ? 'inactive' : 'active';
    try {
      await adminApi.updateChefStatus(chef.id, newStatus);
    } catch { /* fallback */ }
    setChefs(p => p.map(x => x.id === chef.id ? { ...x, isActive: !chef.isActive } : x));
    toast.success(`Chef ${chef.isActive ? 'deactivated' : 'activated'}`);
  };

  const openModal = (row, tab = 'info') => { setSelected(row); setActiveTab(tab); };

  const kitchenColumns = [
    {
      key: 'name', label: 'Restaurant',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={v} />
          <div>
            <div style={{ fontWeight: 700 }}>{v}</div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{row.cuisine}</div>
          </div>
        </div>
      ),
    },
    { key: 'owner', label: 'Owner' },
    { key: 'city', label: 'City' },
    { key: 'rating', label: 'Rating', render: v => <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 700 }}><Star size={12} style={{ fill: '#F59E0B', color: '#F59E0B' }} />{v}</span> },
    { key: 'totalOrders', label: 'Orders', render: v => (v ?? 0).toLocaleString() },
    { key: 'revenue', label: 'Revenue', render: v => formatCurrency(v) },
    { key: 'commission', label: 'Commission', render: v => `${v}%` },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'verified', label: 'Verified', render: v => v ? <span style={{ color: '#10B981', fontWeight: 700 }}>✓ Yes</span> : <span style={{ color: '#EF4444' }}>✗ No</span> },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="primary" size="sm" icon={<ExternalLink size={12} />} onClick={() => navigate(`/admin/restaurants/${row.id}`)}>Details</Button>
          <Button variant="info" size="sm" onClick={() => openModal(row, 'info')}>Info</Button>
          <Button variant={row.status === 'active' ? 'warning' : 'success'} size="sm" onClick={() => toggleStatus(row)}>
            {row.status === 'active' ? 'Disable' : 'Enable'}
          </Button>
        </div>
      ),
    },
  ];

  const chefColumns = [
    {
      key: 'name', label: 'Chef / Staff',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={v} />
          <div>
            <div style={{ fontWeight: 700 }}>{v}</div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{row.email || '—'}</div>
          </div>
        </div>
      ),
    },
    { key: 'phone', label: 'Phone', render: v => v || '—' },
    {
      key: 'role', label: 'Role',
      render: v => (
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6,
          background: v === 'chef' ? '#FFF7ED' : v === 'cook' ? '#F0FDF4' : '#EFF6FF',
          color: v === 'chef' ? '#C2410C' : v === 'cook' ? '#166534' : '#1D4ED8',
          textTransform: 'capitalize',
        }}>{v || '—'}</span>
      ),
    },
    {
      key: 'kitchenName', label: 'Kitchen',
      render: (v, row) => (
        <div>
          <div style={{ fontWeight: 600 }}>{v || '—'}</div>
          <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>{row.kitchenCity || ''}</div>
        </div>
      ),
    },
    { key: 'ordersCompleted', label: 'Orders Done', render: v => <b>{v ?? 0}</b> },
    {
      key: 'performanceRating', label: 'Rating',
      render: v => <span style={{ fontWeight: 700, color: '#F59E0B' }}>⭐ {v ?? 5.0}</span>,
    },
    {
      key: 'isAvailable', label: 'Availability',
      render: v => v
        ? <span style={{ color: '#10B981', fontWeight: 700, fontSize: 12 }}>✅ Available</span>
        : <span style={{ color: '#EF4444', fontWeight: 700, fontSize: 12 }}>❌ Unavailable</span>,
    },
    {
      key: 'isActive', label: 'Status',
      render: v => <Badge status={v ? 'active' : 'inactive'} />,
    },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="info" size="sm" onClick={() => setSelectedChef(row)}>View</Button>
          <Button variant={row.isActive ? 'warning' : 'success'} size="sm" onClick={() => toggleChefStatus(row)}>
            {row.isActive ? 'Disable' : 'Enable'}
          </Button>
        </div>
      ),
    },
  ];

  const mainTabBtn = (tab, label, icon) => (
    <button onClick={() => setMainTab(tab)} style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '10px 24px', cursor: 'pointer', fontWeight: 700, fontSize: 14,
      background: mainTab === tab ? '#E85D04' : 'var(--admin-surface)',
      color: mainTab === tab ? '#fff' : 'var(--admin-text-secondary)',
      border: '1px solid var(--admin-border)',
      borderRadius: 8,
    }}>{icon}{label}</button>
  );

  const tabBtn = (tab, label) => (
    <button onClick={() => setActiveTab(tab)} style={{
      padding: '8px 20px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
      background: 'none', border: 'none',
      borderBottom: activeTab === tab ? '2px solid #E85D04' : '2px solid transparent',
      color: activeTab === tab ? '#E85D04' : 'var(--admin-text-secondary)',
    }}>{label}</button>
  );

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Restaurant Management</h1>
          <p className="admin-page-subtitle">Manage kitchens and kitchen staff across the platform</p>
        </div>
        <div className="admin-page-actions">
          {mainTab === 'kitchens' ? (
            <>
              <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(all, 'kitchens')}>Export</Button>
              <Button variant="primary" icon={<Plus size={14} />} onClick={() => navigate('/admin/restaurants/create')}>Add Restaurant</Button>
            </>
          ) : (
            <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(chefs, 'chefs')}>Export</Button>
          )}
        </div>
      </div>

      {/* Main Tab Switch */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        {mainTabBtn('kitchens', 'Kitchens', <UtensilsCrossed size={16} />)}
        {mainTabBtn('chefs', 'Chefs & Staff', <Users size={16} />)}
      </div>

      {/* ── KITCHENS TAB ──────────────────────────────────────── */}
      {mainTab === 'kitchens' && (
        <>
          <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
            <StatCard label="Total Kitchens" value={all.length} icon={<UtensilsCrossed size={18} />} color="#E85D04" />
            <StatCard label="Active" value={all.filter(r => r.status === 'active').length} icon={<CheckCircle size={18} />} color="#10B981" />
            <StatCard label="Pending" value={all.filter(r => r.status === 'pending').length} icon={<Clock size={18} />} color="#F59E0B" />
            <StatCard label="Inactive" value={all.filter(r => r.status === 'inactive').length} icon={<XCircle size={18} />} color="#6B7280" />
          </div>

          <div className="admin-table-wrapper">
            <div className="admin-table-toolbar">
              <SearchInput value={query} onChange={setQuery} placeholder="Search kitchens..." />
              <Select value={statusFilter} onChange={setStatusFilter} placeholder="All Status"
                options={[{ value: 'active', label: 'Active' }, { value: 'pending', label: 'Pending' }, { value: 'inactive', label: 'Inactive' }]} />
            </div>
            <Table columns={kitchenColumns} data={paginated} loading={loading} />
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={final.length} itemsPerPage={10} onPageChange={goTo} />
          </div>
        </>
      )}

      {/* ── CHEFS TAB ─────────────────────────────────────────── */}
      {mainTab === 'chefs' && (
        <>
          <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
            <StatCard label="Total Staff" value={chefs.length} icon={<Users size={18} />} color="#3B82F6" />
            <StatCard label="Active" value={chefs.filter(c => c.isActive).length} icon={<CheckCircle size={18} />} color="#10B981" />
            <StatCard label="Available Now" value={chefs.filter(c => c.isAvailable).length} icon={<Clock size={18} />} color="#F59E0B" />
            <StatCard label="Chefs" value={chefs.filter(c => c.role === 'chef').length} icon={<Users size={18} />} color="#8B5CF6" />
          </div>

          <div className="admin-table-wrapper">
            <div className="admin-table-toolbar">
              <SearchInput value={chefQuery} onChange={setChefQuery} placeholder="Search chefs, kitchens..." />
              <Select value={chefRoleFilter} onChange={setChefRoleFilter} placeholder="All Roles"
                options={[
                  { value: 'chef', label: 'Chef' },
                  { value: 'cook', label: 'Cook' },
                  { value: 'prep', label: 'Prep' },
                  { value: 'manager', label: 'Manager' },
                ]} />
            </div>
            <Table columns={chefColumns} data={paginatedChefs} loading={chefsLoading} />
            <Pagination currentPage={chefPage} totalPages={chefTotalPages} totalItems={finalChefs.length} itemsPerPage={10} onPageChange={goToChef} />
          </div>
        </>
      )}

      {/* Kitchen Detail Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `${selected.name}` : ''} size="lg"
        footer={<Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>}>
        {selected && (
          <div>
            <div style={{ display: 'flex', borderBottom: '1px solid var(--admin-border)', marginBottom: 20 }}>
              {tabBtn('info', '📋 Kitchen Info')}
              {tabBtn('menu', '🍽️ Menu & Availability')}
              {tabBtn('users', '👤 Kitchen Users')}
            </div>

            {activeTab === 'info' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: 16, background: 'var(--admin-bg)', borderRadius: 10 }}>
                  <Avatar name={selected.name} size={56} />
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>{selected.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>{selected.cuisine} • {selected.city}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                      <Badge status={selected.status} />
                      {selected.verified && <span className="admin-badge" style={{ color: '#10B981', background: '#D1FAE5' }}>✓ Verified</span>}
                    </div>
                  </div>
                </div>
                <div className="admin-grid-2">
                  {[
                    ['Kitchen ID', selected.id], ['Owner', selected.owner], ['Email', selected.email],
                    ['Phone', selected.phone], ['City', selected.city], ['Rating', `⭐ ${selected.rating}`],
                    ['Total Orders', (selected.totalOrders ?? 0).toLocaleString()], ['Revenue', formatCurrency(selected.revenue ?? 0)],
                    ['Commission Rate', `${selected.commission}%`], ['Registered', formatDate(selected.createdAt)],
                  ].map(([l, v]) => (
                    <div key={l} className="admin-info-row">
                      <span className="admin-info-row-label">{l}</span>
                      <span className="admin-info-row-value">{v}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'menu' && <MenuPanel restaurant={selected} />}
            {activeTab === 'users' && <KitchenUsersPanel restaurant={selected} />}
          </div>
        )}
      </Modal>

      {/* Chef Detail Modal */}
      <Modal open={!!selectedChef} onClose={() => setSelectedChef(null)} title="Chef / Staff Details" size="md"
        footer={<Button variant="secondary" onClick={() => setSelectedChef(null)}>Close</Button>}>
        {selectedChef && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: 16, background: 'var(--admin-bg)', borderRadius: 10 }}>
              <Avatar name={selectedChef.name} size={56} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{selectedChef.name}</div>
                <div style={{ fontSize: 13, color: 'var(--admin-text-muted)', textTransform: 'capitalize' }}>{selectedChef.role} — {selectedChef.kitchenName}</div>
                <Badge status={selectedChef.isActive ? 'active' : 'inactive'} />
              </div>
            </div>
            {[
              ['Staff ID', selectedChef.id],
              ['Phone', selectedChef.phone || '—'],
              ['Email', selectedChef.email || '—'],
              ['Role', selectedChef.role],
              ['Kitchen', selectedChef.kitchenName],
              ['Kitchen City', selectedChef.kitchenCity || '—'],
              ['Orders Completed', selectedChef.ordersCompleted ?? 0],
              ['Avg Completion Time', `${selectedChef.avgCompletionTime ?? 0} min`],
              ['Performance Rating', `⭐ ${selectedChef.performanceRating ?? 5.0}`],
              ['Hourly Rate', selectedChef.hourlyRate ? formatCurrency(selectedChef.hourlyRate) : '—'],
              ['Hire Date', formatDate(selectedChef.hireDate)],
              ['Available', selectedChef.isAvailable ? '✅ Yes' : '❌ No'],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-row">
                <span className="admin-info-row-label">{l}</span>
                <span className="admin-info-row-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RestaurantManagement;
